import { useState, useEffect, useCallback } from "react";
import API from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Badge } from "./ui/badge";
import { Switch } from "./ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";
import { Plus, PencilSimple, Trash, Crown, UserGear, FloppyDisk } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function UserPermissionsSection() {
  const [catalog, setCatalog] = useState([]);
  const [roles, setRoles] = useState([]);
  const [usersList, setUsersList] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [perms, setPerms] = useState({});
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showNewRole, setShowNewRole] = useState(false);
  const [newRoleName, setNewRoleName] = useState("");
  const [renaming, setRenaming] = useState(null);
  const [deleting, setDeleting] = useState(null);

  const load = useCallback(async () => {
    const [c, r, u] = await Promise.all([
      API.get("/permissions/catalog"),
      API.get("/permissions/roles"),
      API.get("/permissions/users"),
    ]);
    setCatalog(c.data.groups || []);
    setRoles(r.data.roles || []);
    setUsersList(u.data.users || []);
    return r.data.roles || [];
  }, []);

  useEffect(() => {
    load()
      .then(rs => { if (rs.length) setSelectedId(prev => prev || rs[0].id); })
      .catch(() => toast.error("Failed to load permissions"));
  }, [load]);

  const selected = roles.find(r => r.id === selectedId);

  useEffect(() => {
    if (selected) { setPerms(selected.permissions || {}); setDirty(false); }
  }, [selectedId, roles]); // eslint-disable-line react-hooks/exhaustive-deps

  const isAllowed = (key) => perms[key] !== false;
  const toggle = (key) => { setPerms(p => ({ ...p, [key]: p[key] === false })); setDirty(true); };
  const setGroupAll = (group, val) => {
    setPerms(p => { const n = { ...p }; group.items.forEach(i => { n[i.key] = val; }); return n; });
    setDirty(true);
  };

  const savePerms = async () => {
    setSaving(true);
    try {
      await API.put(`/permissions/roles/${selectedId}`, { permissions: perms });
      toast.success(`Permissions saved for "${selected?.name}"`);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to save"); }
    finally { setSaving(false); }
  };

  const createRole = async () => {
    try {
      const { data } = await API.post("/permissions/roles", { name: newRoleName });
      toast.success(`Role "${data.name}" created — all sections allowed by default`);
      setShowNewRole(false); setNewRoleName("");
      await load(); setSelectedId(data.id);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to create role"); }
  };

  const renameRole = async () => {
    try {
      await API.put(`/permissions/roles/${renaming.id}`, { name: renaming.name });
      toast.success("Role renamed");
      setRenaming(null); await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to rename"); }
  };

  const deleteRole = async () => {
    try {
      const { data } = await API.delete(`/permissions/roles/${deleting.id}`);
      toast.success(`Role deleted — ${data.users_unassigned} user(s) back to full access`);
      setDeleting(null);
      const rs = await load();
      if (selectedId === deleting.id && rs.length) setSelectedId(rs[0].id);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to delete"); }
  };

  const assign = async (userId, roleId) => {
    try {
      const { data } = await API.put("/permissions/assign", { user_id: userId, role_id: roleId === "none" ? null : roleId });
      toast.success(data.message);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to assign role"); }
  };

  const deniedCount = (r) => Object.values(r.permissions || {}).filter(v => v === false).length;

  return (
    <div className="space-y-6" data-testid="user-permissions-section">
      <div>
        <h2 className="text-base font-semibold font-[Chivo] flex items-center gap-2"><UserGear size={18} /> User Permissions</h2>
        <p className="text-xs text-muted-foreground mt-0.5">
          Create roles, toggle which portal sections each role can access, and assign roles to your admin users.
          Users with no role assigned have full access. Changes apply the next time the user loads a page.
        </p>
      </div>

      <div className="flex flex-col lg:flex-row gap-5">
        {/* Roles list */}
        <div className="lg:w-64 shrink-0 space-y-2">
          <div className="flex items-center justify-between">
            <p className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">Roles</p>
            <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setShowNewRole(true)} data-testid="new-role-btn">
              <Plus size={12} className="mr-1" /> New Role
            </Button>
          </div>
          {roles.map(r => (
            <button key={r.id} onClick={() => setSelectedId(r.id)}
              className={`w-full text-left p-3 rounded-md border transition-colors ${selectedId === r.id ? "border-primary bg-primary/5" : "border-border bg-card hover:bg-muted/40"}`}
              data-testid={`role-card-${r.id}`}>
              <div className="flex items-center gap-1.5">
                {r.is_system && <Crown size={14} weight="fill" className="text-amber-500 shrink-0" />}
                <span className="text-sm font-semibold truncate">{r.name}</span>
              </div>
              <p className="text-[11px] text-muted-foreground mt-0.5">
                {r.member_count} member{r.member_count !== 1 ? "s" : ""}
                {r.is_system ? " · Full access" : deniedCount(r) ? ` · ${deniedCount(r)} denied` : " · All allowed"}
              </p>
            </button>
          ))}
        </div>

        {/* Toggles */}
        <div className="flex-1 min-w-0">
          {!selected ? (
            <p className="text-sm text-muted-foreground p-6">Select a role</p>
          ) : selected.is_system ? (
            <div className="border border-amber-300 dark:border-amber-800 rounded-md p-6 bg-amber-50/60 dark:bg-amber-950/20 text-center" data-testid="system-role-banner">
              <Crown size={28} weight="fill" className="text-amber-500 mx-auto mb-2" />
              <h3 className="font-semibold">{selected.name}</h3>
              <p className="text-xs text-muted-foreground mt-1 max-w-md mx-auto">
                Top-tier role with full access to every section of the portal. Toggles are locked and this role cannot be renamed or deleted.
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  <h3 className="font-semibold text-sm">{selected.name}</h3>
                  <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => setRenaming({ id: selected.id, name: selected.name })} data-testid="rename-role-btn"><PencilSimple size={13} /></Button>
                  <Button variant="ghost" size="icon" className="h-6 w-6 text-destructive" onClick={() => setDeleting(selected)} data-testid="delete-role-btn"><Trash size={13} /></Button>
                </div>
                <Button size="sm" onClick={savePerms} disabled={!dirty || saving} data-testid="save-permissions-btn">
                  <FloppyDisk size={14} className="mr-1" /> {saving ? "Saving..." : dirty ? "Save Changes" : "Saved"}
                </Button>
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                {catalog.map(group => (
                  <div key={group.group} className="border border-border rounded-md bg-card" data-testid={`perm-group-${group.group.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                    <div className="flex items-center justify-between px-3 py-2 border-b border-border bg-muted/30">
                      <p className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">{group.group}</p>
                      <div className="flex gap-1">
                        <button className="text-[10px] text-emerald-600 hover:underline" onClick={() => setGroupAll(group, true)}>Allow all</button>
                        <span className="text-[10px] text-muted-foreground">·</span>
                        <button className="text-[10px] text-red-500 hover:underline" onClick={() => setGroupAll(group, false)}>Deny all</button>
                      </div>
                    </div>
                    <div className="divide-y divide-border">
                      {group.items.map(item => (
                        <label key={item.key} className="flex items-center justify-between px-3 py-1.5 cursor-pointer hover:bg-muted/20">
                          <span className={`text-xs ${!isAllowed(item.key) ? "text-muted-foreground line-through" : ""}`}>{item.label}</span>
                          <Switch checked={isAllowed(item.key)} onCheckedChange={() => toggle(item.key)} className="scale-75" data-testid={`perm-toggle-${item.key}`} />
                        </label>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* User assignment */}
      <div>
        <p className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold mb-2">Assign roles to admin users</p>
        <div className="border border-border rounded-md bg-card divide-y divide-border">
          {usersList.map(u => (
            <div key={u.id} className="flex flex-wrap items-center justify-between gap-2 px-4 py-2.5" data-testid={`perm-user-${u.id}`}>
              <div className="min-w-0">
                <p className="text-sm font-medium truncate">{u.name} <span className="text-[10px] text-muted-foreground font-normal">({u.role.replace("_", " ")})</span></p>
                <p className="text-xs text-muted-foreground truncate">{u.email}</p>
              </div>
              <Select value={u.custom_role_id || "none"} onValueChange={v => assign(u.id, v)}>
                <SelectTrigger className="w-52 h-8 text-xs" data-testid={`assign-role-${u.id}`}><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Full access (no role)</SelectItem>
                  {roles.map(r => <SelectItem key={r.id} value={r.id}>{r.is_system ? "👑 " : ""}{r.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          ))}
        </div>
      </div>

      {/* New role dialog */}
      <Dialog open={showNewRole} onOpenChange={setShowNewRole}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>New Role</DialogTitle><DialogDescription>New roles start with every section allowed — turn off what they shouldn't see.</DialogDescription></DialogHeader>
          <Input value={newRoleName} onChange={e => setNewRoleName(e.target.value)} placeholder="e.g. Office Admin" autoFocus data-testid="new-role-name-input" onKeyDown={e => e.key === "Enter" && newRoleName.trim() && createRole()} />
          <DialogFooter><Button onClick={createRole} disabled={!newRoleName.trim()} data-testid="new-role-create-btn">Create Role</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Rename dialog */}
      <Dialog open={!!renaming} onOpenChange={v => !v && setRenaming(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Rename Role</DialogTitle></DialogHeader>
          <Input value={renaming?.name || ""} onChange={e => setRenaming({ ...renaming, name: e.target.value })} autoFocus data-testid="rename-role-input" />
          <DialogFooter><Button onClick={renameRole} disabled={!renaming?.name?.trim()} data-testid="rename-role-save-btn">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <Dialog open={!!deleting} onOpenChange={v => !v && setDeleting(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Delete "{deleting?.name}"?</DialogTitle>
            <DialogDescription>{deleting?.member_count || 0} user(s) assigned to this role will revert to full access.</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleting(null)}>Cancel</Button>
            <Button variant="destructive" onClick={deleteRole} data-testid="delete-role-confirm-btn">Delete Role</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
